/**
 * Company-related Supabase queries
 * Pure async functions - no React, no useQuery
 */

import { supabase } from "@/integrations/supabase/client";
import { getStateSalesTaxRate } from "@/lib/stateSalesTaxRates";

// Types
export interface CompanyInfo {
  id: string;
  name: string;
  restaurant_name: string | null;
  city: string | null;
  state: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  entity_type: string | null;
  ein_number: string | null;
  sales_taxpayer_number: string | null;
  rep_name: string | null;
  rep_title: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompanySettings {
  id: string;
  name: string;
  restaurant_name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  email: string | null;
  phone: string | null;
  sales_taxpayer_number: string | null;
  ein_number: string | null;
  entity_type: string | null;
  accounting_method: string | null;
  logo_url: string | null;
  receipt_email: string | null;
  receipt_email_enabled: boolean | null;
  sales_tax_rate: number | null;
}

/**
 * Get company info by company ID
 * Returns null if company not found or on PGRST116 error
 */
export async function getCompanyInfo(companyId: string): Promise<CompanyInfo | null> {
  if (!companyId) {
    return null;
  }

  const { data, error } = await supabase
    .from("companies")
    .select("id, name, restaurant_name, city, state, email, phone, address, entity_type, ein_number, sales_taxpayer_number, rep_name, rep_title, created_at, updated_at")
    .eq("id", companyId)
    .single();

  if (error) {
    // If company not found, return null instead of throwing
    if (error.code === 'PGRST116') {
      return null;
    }
    throw error;
  }

  return data as CompanyInfo;
}

/**
 * Get company settings by company ID
 * Returns null if company not found
 */
export async function getCompanySettings(companyId: string): Promise<CompanySettings | null> {
  if (!companyId) {
    return null;
  }

  const { data, error } = await supabase
    .from("companies")
    .select("id, name, restaurant_name, address, city, state, zip_code, email, phone, sales_taxpayer_number, ein_number, entity_type, accounting_method, logo_url, receipt_email, receipt_email_enabled, sales_tax_rate")
    .eq("id", companyId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    throw error;
  }

  return data as CompanySettings;
}

/**
 * Get just the company state (for tax rate lookups)
 */
export async function getCompanyState(companyId: string): Promise<string | null> {
  if (!companyId) {
    return null;
  }

  const { data, error } = await supabase
    .from("companies")
    .select("state")
    .eq("id", companyId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    throw error;
  }

  return data?.state ?? null;
}

/**
 * Get the effective sales tax rate for a company.
 * Uses company-level override if set, otherwise falls back to state rate.
 */
export async function getCompanySalesTaxRate(companyId: string): Promise<number> {
  if (!companyId) return 0;

  const { data, error } = await supabase
    .from("companies")
    .select("state, sales_tax_rate")
    .eq("id", companyId)
    .single();

  if (error || !data) return 0;
  if (data.sales_tax_rate != null) return Number(data.sales_tax_rate);
  return getStateSalesTaxRate(data.state);
}
